import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { TRovingTabIndexOptions, TRovingTabIndexReturn } from './types';

/**
 * Manages roving tabindex for ARIA composite widgets (toolbar, listbox).
 * Owns the active index, returns per-item tabIndex values, and handles
 * arrow/Home/End key navigation. Does NOT wrap at ends — stops at boundaries.
 * Clamps active index when itemCount shrinks below it.
 */
const useRovingTabIndex = ({
  itemCount,
  orientation,
  isItemDisabled,
  onActivate,
}: TRovingTabIndexOptions): TRovingTabIndexReturn => {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  /**
   * Finds the first non-disabled item index. Returns -1 if all are disabled.
   */
  const findFirstEnabled = useCallback((): number => {
    for (let i = 0; i < itemCount; i++) {
      if (!isItemDisabled?.(i)) return i;
    }
    return -1;
  }, [itemCount, isItemDisabled]);

  /**
   * Finds the last non-disabled item index. Returns -1 if all are disabled.
   */
  const findLastEnabled = useCallback((): number => {
    for (let i = itemCount - 1; i >= 0; i--) {
      if (!isItemDisabled?.(i)) return i;
    }
    return -1;
  }, [itemCount, isItemDisabled]);

  const [activeIndex, setActiveIndex] = useState<number>(() => findFirstEnabled());

  // INFO: Clamp active index when itemCount shrinks or disabled items change
  useEffect(() => {
    if (itemCount === 0) {
      setActiveIndex(-1);
      return;
    }

    setActiveIndex((prev) => {
      if (prev >= itemCount) {
        // Clamp to last valid enabled item
        return findLastEnabled();
      }
      if (prev === -1 || isItemDisabled?.(prev)) {
        return findFirstEnabled();
      }
      return prev;
    });
  }, [itemCount, isItemDisabled, findFirstEnabled, findLastEnabled]);

  /**
   * Finds the next non-disabled item index in a given direction.
   * Returns the current index if none found (no wrapping).
   */
  const findNext = useCallback(
    (from: number, direction: 1 | -1): number => {
      let candidate = from + direction;
      while (candidate >= 0 && candidate < itemCount) {
        if (!isItemDisabled?.(candidate)) return candidate;
        candidate += direction;
      }
      return from;
    },
    [itemCount, isItemDisabled],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const forwardKeys = orientation === 'horizontal' ? ['ArrowRight'] : ['ArrowDown'];
      const backwardKeys = orientation === 'horizontal' ? ['ArrowLeft'] : ['ArrowUp'];

      if ([...forwardKeys, ...backwardKeys, 'Home', 'End', 'Enter', ' '].includes(e.key)) {
        if ([...forwardKeys, ...backwardKeys, 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
        }

        if (forwardKeys.includes(e.key)) {
          const next = findNext(activeIndex, 1);
          setActiveIndex(next);
          itemRefs.current[next]?.focus();
        } else if (backwardKeys.includes(e.key)) {
          const prev = findNext(activeIndex, -1);
          setActiveIndex(prev);
          itemRefs.current[prev]?.focus();
        } else if (e.key === 'Home') {
          const first = findFirstEnabled();
          if (first !== -1) {
            setActiveIndex(first);
            itemRefs.current[first]?.focus();
          }
        } else if (e.key === 'End') {
          const last = findLastEnabled();
          if (last !== -1) {
            setActiveIndex(last);
            itemRefs.current[last]?.focus();
          }
        } else if (e.key === 'Enter' || e.key === ' ') {
          if (activeIndex >= 0 && !isItemDisabled?.(activeIndex)) {
            onActivate?.(activeIndex);
          }
        }
      }
    },
    [activeIndex, orientation, findNext, findFirstEnabled, findLastEnabled, isItemDisabled, onActivate],
  );

  /**
   * Returns the tabIndex for a given item index.
   * Active item gets 0, all others get -1.
   */
  const getTabIndex = useCallback((index: number): 0 | -1 => (index === activeIndex ? 0 : -1), [activeIndex]);

  /**
   * Returns a stable ref callback for a given item index.
   * Use as `ref={setItemRef(index)}` — the returned function is memoized per index.
   */
  const refCallbacks = useRef<Map<number, (el: HTMLElement | null) => void>>(new Map());
  const setItemRef = useCallback((index: number): ((el: HTMLElement | null) => void) => {
    if (!refCallbacks.current.has(index)) {
      refCallbacks.current.set(index, (el: HTMLElement | null) => {
        itemRefs.current[index] = el;
      });
    }
    return refCallbacks.current.get(index)!;
  }, []);

  return {
    activeIndex,
    handleKeyDown,
    getTabIndex,
    setItemRef,
  };
};

export default useRovingTabIndex;
